import React, { useState, useEffect, useCallback } from 'react';
import {
  UserPlus, Send, CheckCircle, Clock, AlertTriangle, Mail, FileSignature, RefreshCw,
} from 'lucide-react';
import { TERMS_URL, PRIVACY_URL, openPolicy } from '../shared/legal';
import { useLanguage } from '../../components/LanguageContext';

// ============================================================
// The referral partner's three screens: apply, submit a referral, and watch
// what happened to the ones already sent.
//
// The status vocabulary here is OURS, not Close's — see referralRoutes.js.
// An external partner should never read internal pipeline naming, and
// renaming a Close status must not change what they see.
// ============================================================

const API_URL =
  (process.env.REACT_APP_API_URL ||
    'https://investmenter-backend-1081814658115.europe-west1.run.app') + '/api';

const GOLD = '#D6B160';
const INK = '#1a1a1a';

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 16, padding: 24,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
};
const label: React.CSSProperties = {
  display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#333',
};
const input: React.CSSProperties = {
  width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 8,
  fontSize: 14, boxSizing: 'border-box', background: '#fff',
};
const field: React.CSSProperties = { marginBottom: 16 };
const button = (busy: boolean): React.CSSProperties => ({
  width: '100%', padding: '14px 16px', borderRadius: 10, border: 'none',
  background: busy ? '#dcd7cd' : INK, color: busy ? '#8d867b' : GOLD,
  fontWeight: 700, fontSize: 14, cursor: busy ? 'default' : 'pointer',
});
const hero: React.CSSProperties = {
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  borderRadius: 16, padding: 24, color: '#fff', marginBottom: 20,
};
const eyebrow: React.CSSProperties = {
  fontSize: 12, color: GOLD, fontWeight: 600, marginBottom: 4,
  letterSpacing: 1, textTransform: 'uppercase',
};
const wrap: React.CSSProperties = { padding: 16, maxWidth: 600, margin: '0 auto' };

const notice = (kind: 'ok' | 'warn' | 'err'): React.CSSProperties => ({
  padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 500,
  background: kind === 'ok' ? '#dcfce7' : kind === 'warn' ? '#fef3c7' : '#fee2e2',
  color: kind === 'ok' ? '#166534' : kind === 'warn' ? '#92400e' : '#dc2626',
});

// ============================================================
// APPLY — public, no account needed. This is the front door.
// ============================================================
export const ReferralApplyForm: React.FC<{
  onApplied: (email: string, status: string) => void;
  onCancel: () => void;
}> = ({ onApplied, onCancel }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !form.email.trim()) { setErr(t('rfNeedNameEmail')); return; }
    if (!consent) { setErr(t('pnConsentRequired')); return; }
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API_URL}/referral/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          email: form.email.trim().toLowerCase(),
          gdprConsentAt: new Date().toISOString(),
          termsVersion: '1.0',
          privacyVersion: '1.0',
        }),
      });
      const data = await res.json();
      if (data.success) onApplied(form.email.trim().toLowerCase(), data.status);
      else setErr(data.error || t('rfApplyFailed'));
    } catch {
      setErr(t('rfApplyFailed'));
    } finally { setBusy(false); }
  };

  return (
    <div style={wrap}>
      <div style={hero}>
        <div style={eyebrow}>{t('rfApplyEyebrow')}</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>{t('rfApplyTitle')}</h2>
        <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>{t('rfApplySub')}</p>
      </div>

      <form onSubmit={submit} style={card}>
        {err && <div style={notice('err')}>{err}</div>}

        <div style={field}>
          <label style={label} htmlFor="rf-name">{t('rfName')} *</label>
          <input id="rf-name" style={input} value={form.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" />
        </div>
        <div style={field}>
          <label style={label} htmlFor="rf-email">{t('email')} *</label>
          <input id="rf-email" type="email" style={input} value={form.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" />
          <span style={{ fontSize: 11, color: '#999', marginTop: 4, display: 'block' }}>{t('rfEmailHint')}</span>
        </div>
        <div style={field}>
          <label style={label} htmlFor="rf-phone">{t('rfPhone')}</label>
          <input id="rf-phone" type="tel" style={input} value={form.phone} onChange={(e) => set('phone', e.target.value)} autoComplete="tel" />
        </div>
        <div style={field}>
          <label style={label} htmlFor="rf-company">{t('rfCompany')}</label>
          <input id="rf-company" style={input} value={form.company} onChange={(e) => set('company', e.target.value)} autoComplete="organization" />
        </div>

        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '4px 0 18px', fontSize: 12, lineHeight: 1.55, color: '#6f6960', cursor: 'pointer' }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, accentColor: GOLD, flexShrink: 0 }} />
          <span>
            {t('pnConsentPre')}{' '}
            <a href={TERMS_URL} onClick={openPolicy(TERMS_URL)} target="_blank" rel="noopener noreferrer" style={{ color: '#b99a4c' }}>{t('rsTerms')}</a>
            {t('pnConsentMid')}
            <a href={PRIVACY_URL} onClick={openPolicy(PRIVACY_URL)} target="_blank" rel="noopener noreferrer" style={{ color: '#b99a4c' }}>{t('rsPrivacy')}</a>
            {t('pnConsentPost')}
          </span>
        </label>

        <button type="submit" disabled={busy} style={button(busy)}>
          {busy ? t('rfSending') : t('rfApplyCta')}
        </button>
        <button type="button" onClick={onCancel} style={{ width: '100%', marginTop: 10, padding: 12, background: 'none', border: 'none', color: '#6f6960', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {t('rfBack')}
        </button>
      </form>
    </div>
  );
};

// ============================================================
// APPLIED — what the contract step looks like while you wait.
// ============================================================
export const ContractPending: React.FC<{
  email: string;
  status: string;
  onSignIn: () => void;
}> = ({ email, status, onSignIn }) => {
  const { t } = useLanguage();
  const sent = status === 'contract_sent';
  return (
    <div style={wrap}>
      <div style={{ ...card, textAlign: 'center', padding: '40px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          {sent ? <FileSignature size={48} color={GOLD} /> : <Clock size={48} color="#f59e0b" />}
        </div>
        <h3 style={{ fontSize: 19, fontWeight: 700, marginBottom: 8 }}>
          {t(sent ? 'rfContractSentTitle' : 'rfAppliedTitle')}
        </h3>
        <p style={{ fontSize: 13.5, color: '#666', lineHeight: 1.65, maxWidth: 380, margin: '0 auto 6px' }}>
          {t(sent ? 'rfContractSentBody' : 'rfAppliedBody')}
        </p>
        {email && (
          <p style={{ fontSize: 13, fontWeight: 600, color: INK, margin: '10px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Mail size={14} /> {email}
          </p>
        )}
        <div style={{ ...notice('warn'), marginTop: 22, textAlign: 'left', display: 'flex', gap: 10 }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{t('rfContractSpamHint')}</span>
        </div>
        <button onClick={onSignIn} style={{ ...button(false), marginTop: 6 }}>
          {t('rfAlreadySigned')}
        </button>
      </div>
    </div>
  );
};

// ============================================================
// REFER — the partner submits someone. Three fields, as specified.
// ============================================================
export const ReferralSubmitForm: React.FC<{ token: string; onSubmitted: () => void }> = ({ token, onSubmitted }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || !form.email.trim() || form.phone.trim().length < 5) {
      setMsg({ kind: 'err', text: t('rfReferNeedAll') });
      return;
    }
    setMsg(null); setBusy(true);
    try {
      const res = await fetch(`${API_URL}/referral/refer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, email: form.email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (data.success) {
        // A duplicate is still a successful submission — it is recorded as a
        // claim — but it must not read as "we added your lead".
        setMsg({ kind: data.duplicate ? 'warn' : 'ok', text: data.message || t('rfReferOk') });
        setForm({ name: '', email: '', phone: '' });
        if (!data.duplicate) onSubmitted();
      } else {
        setMsg({ kind: 'err', text: data.message || data.error || t('rfReferFailed') });
      }
    } catch {
      setMsg({ kind: 'err', text: t('rfReferFailed') });
    } finally { setBusy(false); }
  };

  return (
    <div style={wrap}>
      <div style={hero}>
        <div style={eyebrow}>{t('rfReferEyebrow')}</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserPlus size={20} /> {t('rfReferTitle')}
        </h2>
        <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>{t('rfReferSub')}</p>
      </div>

      <form onSubmit={submit} style={card}>
        {msg && <div style={notice(msg.kind)}>{msg.text}</div>}

        <div style={field}>
          <label style={label} htmlFor="rf-r-name">{t('rfName')} *</label>
          <input id="rf-r-name" style={input} value={form.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div style={field}>
          <label style={label} htmlFor="rf-r-email">{t('email')} *</label>
          <input id="rf-r-email" type="email" style={input} value={form.email} onChange={(e) => set('email', e.target.value)} />
        </div>
        <div style={field}>
          <label style={label} htmlFor="rf-r-phone">{t('rfPhone')} *</label>
          <input id="rf-r-phone" type="tel" style={input} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
        </div>

        <button type="submit" disabled={busy} style={button(busy)}>
          {busy ? t('rfSending') : (<><Send size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />{t('rfReferCta')}</>)}
        </button>
        <p style={{ fontSize: 11.5, color: '#999', marginTop: 12, lineHeight: 1.5 }}>{t('rfReferConsentNote')}</p>
      </form>
    </div>
  );
};

// ============================================================
// DASHBOARD — the referrals they have sent, and where each one stands.
// ============================================================
type Referral = {
  id: string; name: string; email: string; phone: string;
  status: string; isDuplicate: boolean; createdAt: string;
};

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  submitted: { bg: '#e0e7ff', fg: '#3730a3' },
  contacted: { bg: '#dbeafe', fg: '#1e40af' },
  in_progress: { bg: '#fef3c7', fg: '#92400e' },
  closed: { bg: '#dcfce7', fg: '#166534' },
  not_proceeding: { bg: '#f3f4f6', fg: '#6b7280' },
  duplicate_claim: { bg: '#fee2e2', fg: '#b91c1c' },
};

export const ReferralDashboard: React.FC<{ token: string; reloadKey: number }> = ({ token, reloadKey }) => {
  const { t } = useLanguage();
  const [rows, setRows] = useState<Referral[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/referral/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) { setRows(data.referrals || []); setCounts(data.counts || {}); }
      else setErr(data.message || data.error || t('rfDashFailed'));
    } catch {
      setErr(t('rfDashFailed'));
    } finally { setLoading(false); }
  }, [token, t]);

  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <div style={wrap}>
      <div style={hero}>
        <div style={eyebrow}>{t('rfDashEyebrow')}</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>{t('rfDashTitle')}</h2>
        <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>
          {rows.length} {t(rows.length === 1 ? 'rfDashOne' : 'rfDashMany')}
        </p>
      </div>

      {!loading && rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
          {(['submitted', 'in_progress', 'closed'] as const).map((k) => (
            <div key={k} style={{ ...card, padding: '16px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: INK }}>{counts[k] || 0}</div>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#999', marginTop: 4 }}>
                {t(`rfStatus_${k}`)}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && <div style={{ ...card, textAlign: 'center', color: '#999', fontSize: 13 }}>{t('rfLoading')}</div>}
      {err && <div style={notice('err')}>{err}</div>}

      {!loading && !err && rows.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><UserPlus size={44} color="#d4cec3" /></div>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{t('rfDashEmptyTitle')}</h3>
          <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>{t('rfDashEmptyBody')}</p>
        </div>
      )}

      {rows.map((r) => {
        const tone = STATUS_TONE[r.status] || STATUS_TONE.submitted;
        return (
          <div key={r.id} style={{ ...card, padding: 16, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{r.name}</div>
                <div style={{ fontSize: 12.5, color: '#666', marginTop: 3, wordBreak: 'break-all' }}>{r.email}</div>
                {r.phone && <div style={{ fontSize: 12.5, color: '#666' }}>{r.phone}</div>}
              </div>
              <span style={{ flexShrink: 0, padding: '5px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: tone.bg, color: tone.fg }}>
                {t(`rfStatus_${r.status}`)}
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: '#999', marginTop: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Clock size={12} /> {new Date(r.createdAt).toLocaleDateString()}
              {r.isDuplicate && (
                <span style={{ marginLeft: 'auto', color: '#b91c1c', fontWeight: 600 }}>{t('rfDuplicateFlag')}</span>
              )}
            </div>
          </div>
        );
      })}

      {!loading && (
        <button onClick={load} style={{ width: '100%', marginTop: 8, padding: 12, background: 'none', border: '1px solid #e5e7eb', borderRadius: 10, color: '#6f6960', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <RefreshCw size={13} /> {t('rfRefresh')}
        </button>
      )}
    </div>
  );
};

export const ActiveBadge: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div style={{ ...wrap, paddingBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#dcfce7', color: '#166534', borderRadius: 10, fontSize: 12.5, fontWeight: 600 }}>
        <CheckCircle size={15} /> {t('rfActivePartner')}
      </div>
    </div>
  );
};
